import { useMemo, useState, type FormEvent } from 'react';
import { Wallet, Plus, Trash2, Save, Copy, Check, AlertCircle, QrCode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useCreateWallet,
  useDeleteWallet,
  usePlatformWallets,
  useUpdateWallet,
  type CryptoAsset,
  type CryptoNetwork,
  type CryptoWallet,
} from '@/hooks/useCrypto';
import { ApiError } from '@/lib/api-client';
import { useConfirm } from '@/components/ConfirmDialog';
import { cn, formatDate } from '@/lib/utils';

export function CryptoWalletsPage() {
  const { data, isPending } = usePlatformWallets();

  if (isPending || !data) return <div className="text-muted-foreground">Yükleniyor…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <Wallet className="w-5 h-5" /> Kripto Cüzdanlar
        </h1>
        <p className="text-muted-foreground text-sm">
          Kullanıcıların ödeme yapabileceği cüzdan adreslerini burada tanımla. Sadece "aktif"
          olanlar ödeme ekranında gösterilir. USDT ve USDC destekleniyor; her stablecoin için
          birden fazla ağda farklı adres eklenebilir.
        </p>
      </div>

      <NewWalletForm catalog={data.catalog} />

      <div className="space-y-3">
        <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
          Tanımlı Cüzdanlar ({data.wallets.length})
        </h2>
        {data.wallets.length === 0 ? (
          <Card>
            <CardContent className="py-8 text-center text-sm text-muted-foreground">
              Henüz cüzdan yok. Yukarıdan ekle.
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {data.wallets.map((w) => (
              <WalletCard key={w.id} wallet={w} catalog={data.catalog} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- New wallet form ----------

function NewWalletForm({ catalog }: { catalog: { assets: Array<{ id: string; label: string }>; networks: Array<{ id: string; label: string; short_label: string; memo_supported: boolean }>; supported_combos: Array<{ asset: string; networks: string[] }> } }) {
  const create = useCreateWallet();
  const [asset, setAsset] = useState<CryptoAsset>('USDT');
  const [network, setNetwork] = useState<CryptoNetwork>('tron');
  const [address, setAddress] = useState('');
  const [memo, setMemo] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const allowedNetworks = useMemo(() => {
    const combo = catalog.supported_combos.find((c) => c.asset === asset);
    return combo?.networks ?? [];
  }, [catalog.supported_combos, asset]);

  const currentNetwork = catalog.networks.find((n) => n.id === network);
  const needsMemo = currentNetwork?.memo_supported ?? false;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);
    try {
      await create.mutateAsync({
        asset,
        network,
        address: address.trim(),
        memo: memo.trim() || undefined,
        label: label.trim() || undefined,
        enabled: true,
      });
      setAddress('');
      setMemo('');
      setLabel('');
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Plus className="w-4 h-4" /> Yeni Cüzdan Ekle
        </CardTitle>
        <CardDescription>Aynı adresi farklı ağlara (örn. BSC + Polygon) ayrı ayrı ekleyebilirsin.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="asset">Asset</Label>
              <select
                id="asset"
                value={asset}
                onChange={(e) => {
                  const newAsset = e.target.value as CryptoAsset;
                  setAsset(newAsset);
                  const combo = catalog.supported_combos.find((c) => c.asset === newAsset);
                  if (combo && !combo.networks.includes(network)) {
                    setNetwork(combo.networks[0] as CryptoNetwork);
                  }
                }}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {catalog.assets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.id} — {a.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="network">Ağ</Label>
              <select
                id="network"
                value={network}
                onChange={(e) => setNetwork(e.target.value as CryptoNetwork)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {catalog.networks
                  .filter((n) => allowedNetworks.includes(n.id))
                  .map((n) => (
                    <option key={n.id} value={n.id}>
                      {n.label} ({n.short_label})
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">Cüzdan Adresi</Label>
            <Input
              id="address"
              required
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              className="font-mono text-xs"
              placeholder={network === 'tron' ? 'T...' : network === 'solana' ? 'So...' : '0x...'}
            />
          </div>
          {needsMemo && (
            <div className="space-y-2">
              <Label htmlFor="memo">Memo / Destination Tag (opsiyonel)</Label>
              <Input id="memo" value={memo} onChange={(e) => setMemo(e.target.value)} />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="label">Etiket (opsiyonel, sadece admin görür)</Label>
            <Input
              id="label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Örn: Binance hesabım"
            />
          </div>
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="text-sm text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 rounded-md px-3 py-2">
              Eklendi ✓
            </div>
          )}
          <Button type="submit" disabled={create.isPending}>
            <Save className="w-4 h-4" /> {create.isPending ? 'Ekleniyor…' : 'Ekle'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

// ---------- Wallet card ----------

function WalletCard({
  wallet,
  catalog,
}: {
  wallet: CryptoWallet;
  catalog: { networks: Array<{ id: string; label: string; short_label: string }> };
}) {
  const update = useUpdateWallet();
  const del = useDeleteWallet();
  const confirm = useConfirm();
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);

  const network = catalog.networks.find((n) => n.id === wallet.network);

  const onCopy = async () => {
    await navigator.clipboard.writeText(wallet.address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const onToggle = () => update.mutate({ id: wallet.id, body: { enabled: !wallet.enabled } });

  const onDelete = async () => {
    const ok = await confirm({
      title: 'Cüzdanı sil?',
      description: `${wallet.asset} (${wallet.network.toUpperCase()}) cüzdanı kalıcı olarak silinecek.`,
      confirmText: 'Sil',
      danger: true,
    });
    if (!ok) return;
    del.mutate(wallet.id);
  };

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(wallet.address)}`;

  return (
    <Card className={cn(!wallet.enabled && 'opacity-60')}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold">{wallet.asset}</span>
              <span className="inline-flex items-center rounded-full bg-muted text-muted-foreground text-xs px-2 py-0.5">
                {network?.short_label ?? wallet.network}
              </span>
              {wallet.enabled ? (
                <span className="inline-flex items-center rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 text-xs px-2 py-0.5">
                  AKTİF
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-muted text-muted-foreground text-xs px-2 py-0.5">
                  KAPALI
                </span>
              )}
            </div>
            {wallet.label && <div className="text-xs text-muted-foreground mt-1">{wallet.label}</div>}
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setShowQr((v) => !v)} title="QR göster">
              <QrCode className="w-3.5 h-3.5" />
            </Button>
            <Button size="sm" variant="ghost" onClick={onDelete} disabled={del.isPending} title="Sil">
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        <div className="flex gap-2 items-center">
          <code className="flex-1 min-w-0 font-mono text-xs bg-muted px-2 py-1.5 rounded break-all">
            {wallet.address}
          </code>
          <Button size="sm" variant="outline" onClick={onCopy}>
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </Button>
        </div>

        {wallet.memo && (
          <div className="text-xs">
            <span className="text-muted-foreground">Memo:</span>{' '}
            <code className="font-mono bg-muted px-1 py-0.5 rounded">{wallet.memo}</code>
          </div>
        )}

        {showQr && (
          <div className="flex justify-center">
            <img src={qrUrl} alt="QR" className="w-40 h-40 rounded border bg-white p-2" />
          </div>
        )}

        <div className="flex items-center justify-between pt-2 border-t text-xs text-muted-foreground">
          <span>Eklendi: {formatDate(wallet.created_at)}</span>
          <Button size="sm" variant="ghost" onClick={onToggle} disabled={update.isPending}>
            {wallet.enabled ? 'Devre dışı bırak' : 'Aktif et'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
